/** The small pieces a WIG lane and the Friday board are built from.
 *
 * Every colour here is one of the design system's status tokens and always travels with a
 * word — "Behind pace", "On pace" — so the board reads the same to someone who cannot tell
 * amber from green, and on a projector that washes colour out.
 */

import { useState } from 'react'
import { AlertTriangle, CheckCircle2, CircleDashed, XCircle } from 'lucide-react'

import { formatNumber, progressFraction, type HealthStatus, type LagMeasure, type TrendPoint } from '@/features/wig/useWig'

const HEALTH_TONE: Record<HealthStatus, string> = {
  green: 'var(--status-green)',
  amber: 'var(--status-amber)',
  red: 'var(--status-red)',
}

const HEALTH_LABEL: Record<HealthStatus, string> = {
  green: 'On pace',
  amber: 'Slipping',
  red: 'Behind pace',
}

const HEALTH_ICON = { green: CheckCircle2, amber: AlertTriangle, red: XCircle } as const

/** Health, with its arithmetic one click away. */
export function HealthChip({
  health,
  large = false,
}: {
  health: { status: HealthStatus; reasons: string[] } | null
  large?: boolean
}) {
  const [open, setOpen] = useState(false)
  if (!health) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-pill border border-border px-2 py-0.5 text-xs text-fg-muted"
        title="Health is set from pace once the WIG has at least one lag and one lead measure."
      >
        <CircleDashed size={12} aria-hidden />
        No pace yet
      </span>
    )
  }
  const Icon = HEALTH_ICON[health.status]
  const color = HEALTH_TONE[health.status]
  return (
    <span className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className={`inline-flex items-center gap-1 rounded-pill font-semibold ${
          large ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs'
        }`}
        style={{ color, backgroundColor: `color-mix(in srgb, ${color} 13%, transparent)` }}
        title="Set by the WIG's pace — press to see the arithmetic"
      >
        <Icon size={large ? 15 : 12} aria-hidden />
        {HEALTH_LABEL[health.status]}
      </button>
      {open ? (
        <span className="card absolute right-0 top-8 z-30 block w-[min(24rem,80vw)] space-y-1.5 p-3 text-left text-xs leading-relaxed text-fg-muted shadow-lg">
          <span className="block font-semibold text-fg">How this colour was set</span>
          {health.reasons.map((reason) => (
            <span key={reason} className="block">
              {reason}
            </span>
          ))}
          <span className="block text-[11px]">
            Green at or ahead of pace · amber up to 15% of the goal behind, or a lead measure
            short two weeks running · red beyond 15%.
          </span>
        </span>
      ) : null}
    </span>
  )
}

/**
 * The result against the pace line: a bar for where it is, a tick for where it should be.
 * The gap between the two is the whole point of the board.
 */
export function ScoreBar({ lag, tall = false }: { lag: LagMeasure; tall?: boolean }) {
  const done = progressFraction(lag.current, lag.baseline, lag.target)
  const pace = progressFraction(lag.expected, lag.baseline, lag.target)
  const tone = lag.status ? HEALTH_TONE[lag.status] : 'var(--status-info)'
  return (
    <div
      // The track is a tint of the ink rather than a surface colour, so it shows on a card
      // and on the Friday board's bare page alike.
      className={`relative w-full overflow-visible rounded-pill ${tall ? 'h-4' : 'h-2.5'}`}
      style={{ backgroundColor: 'color-mix(in srgb, var(--fg) 10%, transparent)' }}
      role="img"
      aria-label={`${formatNumber(lag.current)} of ${formatNumber(lag.target)}; pace says ${formatNumber(lag.expected)} by today`}
    >
      <div className="h-full rounded-pill" style={{ width: `${done * 100}%`, backgroundColor: tone }} />
      {lag.expected !== null ? (
        <span
          className="absolute -bottom-1 -top-1 w-0.5 rounded-pill"
          style={{ left: `calc(${pace * 100}% - 1px)`, backgroundColor: 'var(--fg)' }}
          title={`Pace: ${formatNumber(lag.expected)} by today`}
        />
      ) : null}
    </div>
  )
}

/**
 * A quarter's weekly numbers as a thin line, with each week's value on hover. One series,
 * so no legend — the measure's name above it is the label.
 */
export function Sparkline({
  points,
  target,
  width = 120,
  height = 28,
  label,
}: {
  points: TrendPoint[]
  /** A weekly target to draw as a faint reference line (lead measures). */
  target?: number | null
  width?: number
  height?: number
  label: string
}) {
  const [hover, setHover] = useState<number | null>(null)
  const values = points.map((p) => p.value)
  const known = values.filter((v): v is number => v !== null)
  if (known.length < 2) {
    return <span className="text-[11px] text-fg-muted">Trend builds week by week</span>
  }
  const lo = Math.min(...known, target ?? Infinity)
  const hi = Math.max(...known, target ?? -Infinity)
  const span = hi - lo || 1
  const pad = 3
  const x = (i: number) => pad + (i * (width - pad * 2)) / Math.max(1, points.length - 1)
  const y = (v: number) => height - pad - ((v - lo) / span) * (height - pad * 2)

  let path = ''
  values.forEach((v, i) => {
    if (v === null) return
    const prev = i > 0 ? values[i - 1] : null
    path += `${prev === null || i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)} `
  })

  const hovered = hover !== null ? points[hover] : null
  return (
    <span className="relative inline-block">
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={`${label}: weekly trend`}
        onMouseLeave={() => setHover(null)}
      >
        {target !== undefined && target !== null ? (
          <line
            x1={pad}
            x2={width - pad}
            y1={y(target)}
            y2={y(target)}
            stroke="var(--border)"
            strokeDasharray="3 3"
            strokeWidth={1}
          />
        ) : null}
        <path d={path} fill="none" stroke="var(--status-info)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {hover !== null && values[hover] !== null ? (
          <circle cx={x(hover)} cy={y(values[hover] as number)} r={4} fill="var(--status-info)" stroke="var(--surface)" strokeWidth={2} />
        ) : null}
        {/* Hit targets wider than the line: one invisible column per week. */}
        {points.map((point, i) => (
          <rect
            key={point.weekStart}
            x={x(i) - (width - pad * 2) / Math.max(1, points.length - 1) / 2}
            y={0}
            width={(width - pad * 2) / Math.max(1, points.length - 1)}
            height={height}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
          />
        ))}
      </svg>
      {hovered ? (
        <span className="card pointer-events-none absolute -top-7 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap px-2 py-0.5 text-[11px] text-fg shadow">
          w/c {new Date(`${hovered.weekStart}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}:{' '}
          <b>{formatNumber(hovered.value)}</b>
        </span>
      ) : null}
    </span>
  )
}

/**
 * The whiteboard's tally boxes. Pressing box n sets this week's count to n (pressing the
 * last filled box takes one off), so a lead updates the number the way they would tick a
 * whiteboard — without typing.
 */
export function Tally({
  count,
  target,
  onSet,
  disabled = false,
  big = false,
}: {
  count: number
  target: number
  onSet?: (next: number) => void
  disabled?: boolean
  big?: boolean
}) {
  // Past twelve boxes a tally stops being glanceable; show the number instead.
  if (target > 12) {
    return (
      <span className={`font-heading ${big ? 'text-4xl' : 'text-xl'} text-fg`}>
        {formatNumber(count)}
        <span className="text-fg-muted"> / {formatNumber(target)}</span>
      </span>
    )
  }
  const boxes = Math.max(target, count)
  const size = big ? 'h-6 w-6' : 'h-4 w-4'
  return (
    <span className="inline-flex flex-wrap gap-1" role="group" aria-label={`${count} of ${target} this week`}>
      {Array.from({ length: boxes }, (_, i) => {
        const filled = i < count
        const over = i >= target
        const next = filled && i === count - 1 ? i : i + 1
        return (
          <button
            key={i}
            type="button"
            disabled={disabled || !onSet}
            onClick={() => onSet?.(next)}
            aria-label={`Set this week to ${next}`}
            className={`${size} rounded-sm border transition disabled:cursor-default`}
            style={{
              borderColor: filled ? 'var(--status-green)' : 'var(--border)',
              backgroundColor: filled
                ? over
                  ? 'color-mix(in srgb, var(--status-green) 55%, transparent)'
                  : 'var(--status-green)'
                : 'var(--surface)',
            }}
          />
        )
      })}
    </span>
  )
}
